import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useContextSelector } from 'use-context-selector';
import { CartContext } from '@context/cart.context';
import { ImagesService } from '@services/image-service';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { useNegotiation } from '@context/negotiation.context';
import { useBucket } from '@context/bucket.context';
import Dialog from '@components/atoms/dialog/dialog';
import styles from './add-to-cart-modal.module.scss';

interface AddToCartModalProps {
  onCloseDialog?: (arg: boolean) => void;
  onRedirectToCheckout?: () => void;
  showCheckoutModal?: boolean;
}

const AddToCartModal: React.FC<AddToCartModalProps> = (props): React.JSX.Element => {
  const { onCloseDialog, onRedirectToCheckout, showCheckoutModal = true } = props;
  const { t } = useTranslation();
  const { sendAnalyticEvent } = useAnalyticEvents('product');

  const { negotiation: contextNegotiation } = useNegotiation();
  const hasCartUpdateErrorOccurred = useContextSelector(CartContext, (v) => v.hasCartUpdateErrorOccurred);
  const cartUpdateErrorMessage = useContextSelector(CartContext, (v) => v.cartUpdateErrorMessage);
  const { product, isOpen } = useContextSelector(CartContext, (v) => v.cartModalInfo);
  const setCartModalInfo = useContextSelector(CartContext, (v) => v.setCartModalInfo);
  const cartUrl = useContextSelector(CartContext, (v) => v.cartUrl);
  const { isFeatureEnabled } = useBucket();
  const isCheckoutOnePage = isFeatureEnabled((f) => f.smoothCheckoutOnePagerStructure, false);

  const hasPriceReduction = useMemo<boolean>(
    () => product?.regularPrice && product?.regularPrice?.cents !== null && product?.regularPrice?.formatted !== '0',
    [product],
  );

  const negotiation = useMemo<Negotiation | null>(() => {
    return contextNegotiation?.isClosed ? null : contextNegotiation;
  }, [contextNegotiation]);

  const redirectToCheckout = (): void => {
    onRedirectToCheckout?.();
    sendAnalyticEvent('pdp_checkout_popin');
    window.location.href = cartUrl;
  };

  const closeAddToCartModal = (): void => {
    onCloseDialog?.(false);
    sendAnalyticEvent('pdp_continue_shopping');
    setCartModalInfo({ isOpen: false, product: null });
  };

  useEffect(() => {
    sessionStorage?.setItem('isCheckoutOnePage', (!!isCheckoutOnePage).toString());
  }, [isCheckoutOnePage]);

  if (!isOpen) {
    return null;
  }

  return (
    showCheckoutModal && (
      <Dialog
        key={`openAddToCartModal${isOpen}`}
        closeDialog={closeAddToCartModal}
        isOpen={isOpen}
        hasCloseIcon
        styles={{
          content: {
            maxWidth: '800px',
          },
        }}
      >
        {(modalDialogTitleId: string) => (
          <div className="vc-modal__innerContainer">
            <h2
              className="vc-modal__title"
              data-cy="add_to_cart_modal_title"
              id={modalDialogTitleId}
            >
              {hasCartUpdateErrorOccurred
                ? t('PRODUCT.DESCRIPTION.DETAILS.MODAL.ERROR_TITLE')
                : t('PRODUCT.DESCRIPTION.DETAILS.MODAL.SUCCESS_TITLE')}
            </h2>
            <div className="vc-modal__content">
              <div className={styles.addToCart}>
                <div className={styles.addToCart__content}>
                  <div className={styles.addToCart__content__product}>
                    <img
                      className={styles.addToCart__content__product__image}
                      src={`${ImagesService.buildUrl(
                        `/produit/${product.pictures[0].path}`,
                        { w: 100, h: 100 },
                        true,
                      )}`}
                      alt={product.pictures[0].alt}
                      data-cy="add_to_cart_modal_item_image"
                    />

                    {hasCartUpdateErrorOccurred && (
                      <div className={styles.addToCart__error}>
                        <div data-cy="add_to_cart_modal_error_message">
                          {cartUpdateErrorMessage ?? 'PRODUCT.DESCRIPTION.DETAILS.MODAL.TECHNICAL_PROBLEM'}
                        </div>
                      </div>
                    )}

                    {!hasCartUpdateErrorOccurred && (
                      <div
                        className={styles.addToCart__content__product__description}
                        data-cy="add_to_cart_modal_item_description"
                      >
                        <ul className={styles.addToCart__content__product__title}>
                          <li
                            className={styles.addToCart__content__product__brand}
                            data-cy="add_to_cart_modal_item_brand"
                          >
                            {product.brand.name}
                          </li>
                          <li
                            className={styles.addToCart__content__product__category}
                            data-cy="add_to_cart_modal_item_category"
                          >
                            {product.category.localizedName}
                          </li>
                        </ul>
                        <div className={styles.addToCart__content__product__price}>
                          {product.price.formatted && (
                            <p>
                              {hasPriceReduction && (
                                <span
                                  className={styles['price--strikeOut']}
                                  data-cy="add_to_cart_modal_item_price"
                                >
                                  {product.regularPrice.formatted}
                                </span>
                              )}
                              <span
                                className={clsx({
                                  [styles['price--promo']]: hasPriceReduction,
                                })}
                                data-cy="add_to_cart_modal_item_reduced_price"
                              >
                                {negotiation && negotiation.isAccepted
                                  ? negotiation.price.formatted
                                  : product.price.formatted}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.addToCart__actions}>
                  <Button
                    disabled={hasCartUpdateErrorOccurred}
                    variant="primary"
                    size="large"
                    fullWidth
                    notFullWidthQuery="md-up"
                    className={clsx(styles.addToCart__actions__button, styles['addToCart__actions__button--checkout'])}
                    onClick={redirectToCheckout}
                    data-cy="add_to_cart_modal_show_cart_button"
                    data-vc-dd-action-name="proceed_to_checkout_from_cart_modal"
                  >
                    {t('PRODUCT.DESCRIPTION.DETAILS.MODAL.SHOW_CART')}
                  </Button>

                  <Button
                    type="submit"
                    variant="secondary"
                    size="large"
                    fullWidth
                    notFullWidthQuery="md-up"
                    className={clsx(styles.addToCart__actions__button, styles['addToCart__actions__button--shop'])}
                    onClick={closeAddToCartModal}
                    data-cy="add_to_cart_modal_continue_shopping"
                    data-vc-dd-action-name="continue_shopping_from_cart_modal"
                  >
                    {t('PRODUCT.DESCRIPTION.DETAILS.MODAL.CONTINUE_SHOPPING')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    )
  );
};

export default AddToCartModal;
